import { useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, Trash, Package } from '@phosphor-icons/react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import MasterItemPicker from '@/components/master/MasterItemPicker';
import { devApi } from './devApi';

const API = process.env.REACT_APP_BACKEND_URL;

// 材料として選べる製造品目の種別。原材料・副資材・購入部品まで。
// 完成品や仕掛品を材料として選ばせない。
// この絞りは mfg（製造品目）の行にだけ効く。技術資産（eng_assets）には
// item_type に相当する項目が存在せず、master-search も eng 行には item_type を
// 返さないため、同じ絞りを当てると技術資産が1件も出なくなる。
export const MATERIAL_ITEM_TYPES = ['raw', 'sub_material', 'purchased_part'];

// 材料に選べるマスター。製造品目（買う/作る物）と技術資産（社内の現物在庫）。
// IT資産・備品は入れない。どちらも個体の貸出/管理台帳で、製作で削り取られる
// 数量を持たない（備品の消耗品でさえ事務用品の払い出しで、製作材料ではない）。
export const MATERIAL_SOURCES = ['mfg', 'eng'];

// 保存済み行の出所表示。master-search の source_label と同じ語を使う。
export const MATERIAL_SOURCE_LABELS = { mfg: '製造', eng: '技術資産' };

export const emptyMaterialLine = () => ({
  component_item_id: '', component_source: 'mfg',
  component_name: '', component_sku: '',
  qty: '', qty_basis: 'per_unit', uom: '', notes: '',
});

export const isResolved = (l) => !!(l.component_item_id && Number(l.qty) > 0 && l.uom);

const BASIS = [{ v: 'per_unit', l: '1台あたり' }, { v: 'total', l: '合計' }];

/**
 * 使用材料（自社製作の未登録部品のみ）。
 * ・材料は必ず検索結果から選ぶ（手入力では保存できない）
 * ・単位は mfg_uoms（製造の単位マスタ）だけ。shizai_lookup_uoms とは別物なので
 *   既存の LookupCombobox kind="uoms" は使わない。
 * ・数量は「利用者が知っている数」＋「その基準」を持つだけ。計算しない。
 *
 * 技術資産（eng）の行について:
 *   技術資産由来の材料行は mfg_boms.components[].component_item_id には**なれない**。
 *   製造の部品表が参照できるのは mfg_items だけで、eng_assets の id を入れると
 *   参照先の無い構成になる。昇格（BOM化）の面倒を見る仕組みはここには無く、
 *   この画面は「この部品が何を食ったか」の記録に留める。
 */
export const MaterialLines = ({ lines, onChange, locked = false }) => {
  const [uoms, setUoms] = useState([]);

  useEffect(() => {
    axios.get(`${API}/api/mfg/uoms`, { withCredentials: true })
      .then((r) => setUoms((r.data || []).map((u) => u.name).filter(Boolean)))
      .catch(() => setUoms([]));
  }, []);

  const set = (i, patch) => onChange(lines.map((l, k) => (k === i ? { ...l, ...patch } : l)));
  const add = () => onChange([...lines, emptyMaterialLine()]);
  const remove = (i) => onChange(lines.filter((_, k) => k !== i));

  return (
    <div className="rounded-xl border border-sky-200 bg-sky-50/40 p-3" data-testid="dev-part-materials">
      <div className="flex items-center justify-between">
        <Label className="flex items-center gap-1.5 text-xs font-semibold text-zinc-700">
          <Package size={14} weight="duotone" className="text-sky-600" />
          使用材料
          <span className="rounded bg-zinc-100 px-1 text-[10px] font-medium text-zinc-500">任意</span>
        </Label>
        {!locked && (
          <Button type="button" size="sm" variant="outline" onClick={add}
            data-testid="dev-part-material-add" className="h-7 gap-1 text-[11px]">
            <Plus size={12} /> 材料を追加
          </Button>
        )}
      </div>

      {locked && (
        <p className="mt-1.5 rounded bg-white/80 px-2 py-1 text-[10px] leading-snug text-zinc-600"
          data-testid="dev-part-materials-locked-note">
          技術資産として登録済みのため編集できません。ここから先は構成の管理が下流（製造の部品表）に移ります。
        </p>
      )}

      {lines.length === 0 && !locked && (
        <p className="mt-1.5 text-[10px] text-zinc-500" data-testid="dev-part-materials-empty">
          材料が未定なら空のままで保存できます。
        </p>
      )}

      <div className="mt-2 space-y-2">
        {lines.map((l, i) => (
          <div key={i} className="rounded-lg border border-zinc-200 bg-white p-2.5"
            data-testid={`dev-part-material-row-${i}`}>
            {l.component_item_id ? (
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-800"
                  data-testid={`dev-part-material-picked-${i}`}>
                  {l.component_sku ? `${l.component_sku} — ` : ''}{l.component_name}
                </span>
                {/* どのマスターから取った材料かを必ず見せる。製造品目と技術資産は
                    在庫の持ち主も補充の経路も別物で、品名だけでは区別できない。 */}
                <span className="text-[10px] text-zinc-600"
                  data-testid={`dev-part-material-source-${i}`}>
                  {MATERIAL_SOURCE_LABELS[l.component_source] || l.component_source}
                </span>
                {!locked && (
                  <button type="button" className="text-[10px] text-zinc-500 underline"
                    data-testid={`dev-part-material-clear-${i}`}
                    onClick={() => set(i, { component_item_id: '', component_source: 'mfg',
                      component_name: '', component_sku: '' })}>
                    選び直す
                  </button>
                )}
              </div>
            ) : (
              <MasterItemPicker
                label="材料をマスターから選ぶ"
                placeholder="品名・品番で検索（製造品目・技術資産）"
                helper="手入力では登録できません。検索結果から選んでください。候補の右端に出所（製造／技術資産）が出ます。"
                search={devApi.masterSearch}
                sources={MATERIAL_SOURCES}
                itemTypes={MATERIAL_ITEM_TYPES}
                testIdPrefix={`dev-part-material-${i}`}
                onPick={(r) => set(i, {
                  component_item_id: r.id, component_source: r.source === 'eng' ? 'eng' : 'mfg',
                  component_name: r.name, component_sku: r.sku || '',
                  uom: l.uom || (uoms.includes(r.uom) ? r.uom : ''),
                })}
              />
            )}

            <div className="mt-2 grid grid-cols-12 gap-2">
              <div className="col-span-3">
                <Label className="text-[10px] font-semibold text-zinc-600">数量</Label>
                <Input type="number" min="0" step="any" value={l.qty} disabled={locked}
                  onChange={(e) => set(i, { qty: e.target.value })}
                  className="mt-0.5 h-9 tabular-nums" data-testid={`dev-part-material-qty-${i}`} />
              </div>
              <div className="col-span-4">
                <Label className="text-[10px] font-semibold text-zinc-600">数量の基準</Label>
                <div className="mt-0.5 inline-flex overflow-hidden rounded-md border border-zinc-200">
                  {BASIS.map((b) => (
                    <button key={b.v} type="button" disabled={locked}
                      onClick={() => set(i, { qty_basis: b.v })}
                      data-testid={`dev-part-material-basis-${b.v}-${i}`}
                      className={`px-2.5 py-1.5 text-[11px] transition-colors ${
                        l.qty_basis === b.v ? 'bg-sky-600 text-white'
                          : 'bg-white text-zinc-600 hover:bg-zinc-50'}`}>
                      {b.l}
                    </button>
                  ))}
                </div>
              </div>
              <div className="col-span-3">
                <Label className="text-[10px] font-semibold text-zinc-600">単位</Label>
                {/* 製造の単位マスタ（mfg_uoms）だけ。自由入力させない。 */}
                <select value={l.uom} disabled={locked}
                  onChange={(e) => set(i, { uom: e.target.value })}
                  className="mt-0.5 h-9 w-full rounded-md border border-zinc-200 bg-white px-2 text-sm disabled:bg-zinc-50"
                  data-testid={`dev-part-material-uom-${i}`}>
                  <option value="">選択</option>
                  {uoms.map((u) => <option key={u} value={u}>{u}</option>)}
                </select>
              </div>
              <div className="col-span-2 flex items-end justify-end">
                {!locked && (
                  <Button type="button" size="sm" variant="ghost" onClick={() => remove(i)}
                    data-testid={`dev-part-material-remove-${i}`}
                    className="h-9 text-rose-600 hover:bg-rose-50">
                    <Trash size={14} />
                  </Button>
                )}
              </div>
            </div>

            <Input value={l.notes || ''} disabled={locked}
              onChange={(e) => set(i, { notes: e.target.value })}
              placeholder="備考（板厚・支給元など）" className="mt-2 h-8 text-[12px]"
              data-testid={`dev-part-material-notes-${i}`} />

            {!locked && !isResolved(l) && (
              <p className="mt-1 text-[10px] text-amber-700" data-testid={`dev-part-material-incomplete-${i}`}>
                材料・数量・単位がそろっていない行は保存されません。
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default MaterialLines;
